import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  bulkSendDocuments,
  deleteDocument,
  getDocumentById,
  getProjectDocuments,
  getVersionHistory,
  sendDocumentWhatsApp,
  updateDocument,
  uploadDocument,
} from "../../services/api/api";

/**
 * Hook para obter visão geral dos documentos do projeto
 * @param {number} projectId - ID do projeto
 * @param {boolean} enabled - Se a query deve ser executada
 * @returns {object} - { data, isLoading, error, refetch }
 */
export const useProjectDocuments = (projectId, enabled = true) => {
  return useQuery(
    ["projectDocuments", projectId],
    () => getProjectDocuments(projectId),
    {
      enabled: !!projectId && enabled,
      staleTime: 1000 * 60 * 5, // 5 minutos
      retry: 1,
    }
  );
};

/**
 * Hook para buscar documento específico
 * @param {number} documentId - ID do documento
 * @param {boolean} enabled - Se a query deve ser executada
 * @returns {object} - { data, isLoading, error }
 */
export const useDocumentById = (documentId, enabled = true) => {
  return useQuery(
    ["document", documentId],
    () => getDocumentById(documentId),
    {
      enabled: !!documentId && enabled,
      staleTime: Infinity,
      retry: 1,
    }
  );
};

/**
 * Hook para obter histórico de versões
 * @param {number} projectId - ID do projeto
 * @param {number} documentTypeId - ID do tipo de documento
 * @param {boolean} enabled - Se a query deve ser executada
 * @returns {object} - { data, isLoading, error }
 */
export const useVersionHistory = (projectId, documentTypeId, enabled = true) => {
  return useQuery(
    ["versionHistory", projectId, documentTypeId],
    () => getVersionHistory(projectId, documentTypeId),
    {
      enabled: !!projectId && !!documentTypeId && enabled,
      staleTime: 1000 * 60 * 2, // 2 minutos
      retry: 1,
    }
  );
};

/**
 * Hook para upload de documento
 * @returns {object} - { mutate, mutateAsync, isLoading, error }
 */
export const useUploadDocument = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ projectId, documentTypeId, file, comments, onUploadProgress }) =>
      uploadDocument(projectId, documentTypeId, file, comments, onUploadProgress),
    {
      onSuccess: (data, variables) => {
        // Invalida cache dos documentos do projeto
        queryClient.invalidateQueries(["projectDocuments", variables.projectId]);
        queryClient.invalidateQueries(["versionHistory", variables.projectId, variables.documentTypeId]);
        queryClient.invalidateQueries(["projectActivityFeed", variables.projectId]);
      },
    }
  );
};

/**
 * Hook para atualizar comentários de documento
 * @returns {object} - { mutate, mutateAsync, isLoading, error }
 */
export const useUpdateDocument = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ documentId, comments }) => updateDocument(documentId, comments),
    {
      onSuccess: (data, variables) => {
        // Atualiza cache do documento específico
        queryClient.invalidateQueries(["document", variables.documentId]);
        // Atualiza lista geral
        queryClient.invalidateQueries(["projectDocuments"]);
      },
    }
  );
};

/**
 * Hook para deletar documento
 * @returns {object} - { mutate, mutateAsync, isLoading, error }
 */
export const useDeleteDocument = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (documentId) => deleteDocument(documentId),
    {
      onSuccess: () => {
        // Invalida cache de documentos de projeto
        queryClient.invalidateQueries(["projectDocuments"]);
        queryClient.invalidateQueries(["versionHistory"]);
      },
    }
  );
};

/**
 * Hook para enviar documento via WhatsApp
 * @returns {object} - { mutate, mutateAsync, isLoading, error }
 */
export const useSendDocumentWhatsApp = () => {
  return useMutation(
    ({ documentId, recipientPhone, recipientName }) =>
      sendDocumentWhatsApp(documentId, recipientPhone, recipientName)
  );
};

/**
 * Hook para envio em lote via WhatsApp
 * @returns {object} - { mutate, mutateAsync, isLoading, error }
 */
export const useBulkSendDocuments = () => {
  return useMutation(
    ({ projectId, documentIds, recipientPhone, recipientName }) =>
      bulkSendDocuments(projectId, documentIds, recipientPhone, recipientName)
  );
};

